import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {map, switchMap, tap} from 'rxjs/operators';
import {isPlatformBrowser} from '@angular/common';
import {ProductService} from '../../networking/services/product/product.service';
import {CustomerManager} from '../customer/customer/customer.manager';
import {Product} from '../../networking/models/product/responses/product.class';
import {LocationManager} from '../location/location.manager';
import {LocalStorageManager} from '../local-storage/local-storage.manager';
import { TalonOneService } from '../../networking/services/talon-one/talon-one.service';
import { SessionStorageManager } from '../session-storage/session-storage.manager';

@Injectable({
  providedIn: 'root',
})
export class ProductManager {
  isBrowser;
  constructor(
    private productService: ProductService,
    private lsm: LocalStorageManager,
    private customerManager: CustomerManager,
    private location: LocationManager,
    @Inject(PLATFORM_ID) platformId,
    private talonOneService: TalonOneService,
    private ssm: SessionStorageManager
) { this.isBrowser = isPlatformBrowser(platformId) && !(navigator.userAgent.indexOf('Chrome-Lighthouse') > -1); }

  getItem(params): Observable<any> {
    params = {
      ...params,
      idStoreGroup: this.location.getIdStoreGroup(),
    };
    return this.productService.getItem(params).pipe(
      map(res => {
        if (res.crossSales) {
          res.crossSales = res.crossSales.map(item => new Product(item));
        }
        if (res.substitutes) {
          res.substitutes = res.substitutes.map(item => new Product(item));
        }
        return res;
      }),
    );
  }

  getItemV2(params): Observable<any> {
    const customer = this.lsm.get('customer');

    if (!customer) {
      return of().pipe(
        switchMap(() => throwError('No customer')),
      );
    }

    params = {
      source: this.isBrowser ? (window.innerWidth < 992 ? 'RESPONSIVE' : 'WEB') : 'WEB',
      idItem: params,
      idCustomerWebSafe: customer?.idCustomerWebSafe,
      idStoreGroup: this.location.getIdStoreGroup(),
    };
    const nearbyStores = this.location.getNearbyStores();

    if (nearbyStores) {
      params = {
        ...params,
        nearbyStores,
      };
    }

    return this.productService.getItemV2(params)
      .pipe(map(response => {
        const source = response.itemSection;
        const itemSeo = source.filter(type => type.componentType === 'ITEM_SEO');
        const itemList = source.filter(type => type.componentType?.includes('ITEM_LIST'));
        if (itemSeo[0]?.html) {
          itemSeo[0].html = atob(itemSeo[0].html);
        }
        if (itemList) {
          itemList.map(item => {
            const array = [];
            return !item.list ? [] : item.list.map(i => {
              if (i.type === 'UNIQUE') {
                i.product = i.product?.[0] ? new Product(i.product[0]) : new Product(i.product);
                array.push(i.product);
              }
              if (i.action === 'SUGGESTED') {
                i.url = `/sugeridos/${i.id}`;
                i = new Product(i);
                array.push(i);
              }
              if (i.action === 'HIGHLIGHTED') {
                i.url = `/destacados/${i.id}`;
                i = new Product(i);
                array.push(i);
              }
              item.products = array;
              return i;
            });
          });
        }
        return response;
      }));
  }

  getFiltersItems(): Observable<any> {
    const user = this.customerManager.customer;
    const data = {
      tokenIdWebSafe: user.token.tokenIdWebSafe,
      token: user.token.token,
    };
    return this.productService.getFiltersItems(data);
  }

  getItemAvailable(id): Observable<any> {
    const params = {idItem: id};
    return this.productService.getItemAvailable(params);
  }

  getDiscounts(items): Observable<any> {
    const user = this.customerManager.customer;
    const data = {
      idCustomer: user?.id || 0,
      idCustomerWebSafe: user?.idCustomerWebSafe,
      token: user?.token?.token,
      tokenIdWebSafe: user?.token?.tokenIdWebSafe,
      idSession: user?.token?.tokenIdWebSafe,
      profileId: user?.analyticsUUID || user?.token?.tokenIdWebSafe,
      items,
    };
    return this.productService.getDiscounts(data);
  }

  getAnonymousItemsTalonOne():Observable<any>{
    let itemsTalonOne = this.ssm.get( 'itemsTalonOneAnonymous' );

    if( !itemsTalonOne ){
      return this.talonOneService.getItemsAnonymousTalonOne()
        .pipe(tap( items => this.ssm.set( 'itemsTalonOneAnonymous', items ) ));
    }
    return of(itemsTalonOne);
  }
}
